import { useRef, useEffect } from "react";

interface MfaCodeInputProps {
  value: string[];
  onChange: (code: string[]) => void;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}

export default function MfaCodeInput({
  value,
  onChange,
  onComplete,
  autoFocus = true,
  disabled = false,
  hasError = false,
}: MfaCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index: number, inputValue: string) => {
    if (!/^\d*$/.test(inputValue)) return;

    const newCode = [...value];
    newCode[index] = inputValue.slice(-1);
    onChange(newCode);

    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((digit) => digit !== "") && onComplete) {
      onComplete(newCode.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      const newCode = [...value];
      newCode[index - 1] = "";
      onChange(newCode);
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2">
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          disabled={disabled}
          className={`w-12 h-12 text-center text-xl border-2 rounded-md focus:outline-none disabled:opacity-50 text-[var(--color-foreground-neutral-bold)] bg-[var(--color-background-neutral-subtlest)] ${
            hasError
              ? "border-[var(--color-border-critical-bold)]"
              : "border-[var(--color-border-neutral-subtle)] focus:border-[var(--color-border-primary-bold)]"
          }`}
          autoFocus={index === 0 && autoFocus}
        />
      ))}
    </div>
  );
}
